import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import {
  ArrowSquareOutIcon,
  SparkleIcon,
  TrashIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

interface NamiPanelHeaderProps {
  /** Module-provided icon; the sparkle when absent. */
  icon?: ReactNode;
  /** Module-provided title; "Nami" when absent. */
  title?: string;
  /** One line under the title: what this page's surface is about. */
  subtitle?: string;
  onClear: () => void;
  clearDisabled: boolean;
  onClose: () => void;
  /** Whether to offer the jump to the full-page chat. */
  showFullChatLink: boolean;
}

/**
 * Header of the docked Nami panel: identity, the page context the agent is
 * working with, and the panel controls. Icon-only buttons are named for
 * assistive technology, not only by tooltip.
 */
export function NamiPanelHeader({
  icon,
  title,
  subtitle,
  onClear,
  clearDisabled,
  onClose,
  showFullChatLink,
}: NamiPanelHeaderProps) {
  const gt = useGT();
  const clearLabel = gt('Clear conversation', {
    context: 'tooltip for button that clears chat history',
  });
  const closeLabel = gt('Close', {
    context: 'tooltip for button that closes the chat agent panel',
  });
  const fullChatLabel = gt('Open full chat', {
    context: 'link from the docked AI assistant panel to the full-page chat',
  });

  return (
    <div className="flex shrink-0 items-start justify-between gap-2 border-b px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        {icon ?? (
          <SparkleIcon
            weight="fill"
            aria-hidden
            className="size-4 shrink-0 text-purple-600 dark:text-purple-400"
          />
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">
            {title ?? (
              <T context="default title of the AI assistant side panel">
                AI assistant
              </T>
            )}
          </div>
          {subtitle && (
            <div className="truncate text-xs text-muted-foreground">
              {subtitle}
            </div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {showFullChatLink && (
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-7 w-7 p-0"
            aria-label={fullChatLabel}
            title={fullChatLabel}
          >
            <Link to="/chat" onClick={onClose}>
              <ArrowSquareOutIcon className="size-3.5" />
            </Link>
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          disabled={clearDisabled}
          className="h-7 w-7 p-0"
          aria-label={clearLabel}
          title={clearLabel}
        >
          <TrashIcon className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-7 w-7 p-0"
          aria-label={closeLabel}
          title={closeLabel}
        >
          <XIcon className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}
